/**
 * Decision Layer - Distinctive Visual Motif
 * Subtle layered contours/gridlines suggesting prioritization + guardrails
 * NOT stock noise - handcrafted decision field
 */

import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

interface DecisionLayerProps {
  className?: string;
  intensity?: 'subtle' | 'medium' | 'strong';
}

export function DecisionLayer({ className = '', intensity = 'subtle' }: DecisionLayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Subtle parallax drift
  const driftX = useTransform(scrollYProgress, [0, 1], ['0%', '2%']);
  const driftY = useTransform(scrollYProgress, [0, 1], ['0%', '1%']);

  const opacityMap = {
    subtle: 'opacity-[0.02]',
    medium: 'opacity-[0.04]',
    strong: 'opacity-[0.06]',
  };

  return (
    <div ref={containerRef} className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`}>
      {/* Base grid layer - refined with diagonal accent lines */}
      <motion.svg
        className={`absolute inset-0 w-full h-full ${opacityMap[intensity]}`}
        style={{ x: driftX, y: driftY }}
        preserveAspectRatio="none"
        viewBox="0 0 1200 800"
      >
        <defs>
          <pattern
            id="decision-grid"
            x="0"
            y="0"
            width="72"
            height="72"
            patternUnits="userSpaceOnUse"
          >
            {/* Primary grid lines - more refined */}
            <line
              x1="0"
              y1="0"
              x2="72"
              y2="0"
              stroke="currentColor"
              strokeWidth="0.4"
              className="text-foreground"
            />
            <line
              x1="0"
              y1="0"
              x2="0"
              y2="72"
              stroke="currentColor"
              strokeWidth="0.4"
              className="text-foreground"
            />
            {/* Subtle diagonal accent - signature detail */}
            <line
              x1="0"
              y1="0"
              x2="72"
              y2="72"
              stroke="currentColor"
              strokeWidth="0.15"
              className="text-primary/20"
              opacity="0.3"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#decision-grid)" />
      </motion.svg>

      {/* Contour arcs - refined with decision layer motif */}
      <motion.svg
        className={`absolute inset-0 w-full h-full ${opacityMap[intensity]}`}
        style={{ x: driftX, y: driftY }}
        preserveAspectRatio="none"
        viewBox="0 0 1200 800"
      >
        <defs>
          <linearGradient id="contour-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.08" />
            <stop offset="50%" stopColor="currentColor" stopOpacity="0.03" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.08" />
          </linearGradient>
          <linearGradient id="contour-primary" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.06" />
            <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.02" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.06" />
          </linearGradient>
        </defs>
        {/* Refined concentric arcs - decision layers */}
        <path
          d="M 0 400 Q 300 200, 600 400 T 1200 400"
          fill="none"
          stroke="url(#contour-gradient)"
          strokeWidth="0.8"
          className="text-foreground"
        />
        <path
          d="M 0 500 Q 400 300, 800 500 T 1200 500"
          fill="none"
          stroke="url(#contour-gradient)"
          strokeWidth="0.8"
          className="text-foreground"
        />
        {/* Primary accent arc - signature decision layer */}
        <path
          d="M 0 350 Q 250 150, 500 350 T 1000 350 T 1200 350"
          fill="none"
          stroke="url(#contour-primary)"
          strokeWidth="1"
          className="text-primary"
        />
      </motion.svg>

      {/* Risk glow accent - subtle amber for revenue at risk */}
      <motion.div
        className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl"
        style={{
          background: 'radial-gradient(circle, rgba(251, 191, 36, 0.08) 0%, transparent 70%)',
          x: driftX,
          y: driftY,
        }}
      />
    </div>
  );
}

/**
 * Risk Glow - Amber accent for revenue at risk indicators
 */
export function RiskGlow({ className = '' }: { className?: string }) {
  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      <div className="absolute inset-0 bg-gradient-radial from-amber-500/10 via-amber-500/5 to-transparent blur-2xl" />
    </div>
  );
}

